import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { BarChart3, TrendingUp, TrendingDown, Award, Target, Calendar, RefreshCw, Building, Shield, AlertTriangle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SafetyScorecardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [trends, setTrends] = useState([]);
  const [daysWithoutIncident, setDaysWithoutIncident] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [metricsForm, setMetricsForm] = useState({
    total_hours_worked: 0,
    total_employees: 0,
    recordable_incidents: 0,
    dart_cases: 0,
    days_away: 0,
    restricted_days: 0,
    fatalities: 0,
    near_misses: 0,
    emr: 1.0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchCompanies();
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchData();
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [metricsRes, benchmarkRes, trendsRes, daysRes] = await Promise.all([
        axios.get(`${API}/safety-scorecard/metrics/${selectedCompany}`),
        axios.get(`${API}/safety-scorecard/benchmark/compare/${selectedCompany}`),
        axios.get(`${API}/safety-scorecard/trends/${selectedCompany}`),
        axios.get(`${API}/safety-scorecard/days-without-incident/${selectedCompany}`)
      ]);
      setMetrics(metricsRes.data);
      setBenchmark(benchmarkRes.data);
      setTrends(trendsRes.data.trends || []);
      setDaysWithoutIncident(daysRes.data);
      setMetricsForm(metricsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/safety-scorecard/leaderboard`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const handleUpdateMetrics = async () => {
    try {
      await axios.post(`${API}/safety-scorecard/metrics/${selectedCompany}`, metricsForm);
      toast({ title: 'Success', description: 'Metrics updated successfully!' });
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update metrics', variant: 'destructive' });
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    if (score >= 50) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getGradeColor = (grade) => {
    const colors = { A: 'bg-green-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', F: 'bg-red-500' };
    return colors[grade] || 'bg-gray-500';
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Safety Scorecard</h1>
              <p className="text-sm text-slate-300">Track and benchmark your safety performance</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-[200px] bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Select Company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={fetchData} variant="outline" className="text-white border-white/20 hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Days Without Incident Banner */}
      {daysWithoutIncident && (
        <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Days Without Recordable Incident</p>
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl font-bold">{daysWithoutIncident.days_without_incident}</span>
                  <Badge className="bg-white/20">{daysWithoutIncident.milestone}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-green-100 text-sm">Next Milestone</p>
                <p className="text-xl font-semibold">{daysWithoutIncident.next_milestone?.milestone}</p>
                <p className="text-green-100">{daysWithoutIncident.next_milestone?.days_needed} days to go</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scorecard Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Safety Score</p>
                <p className="text-3xl font-bold">{metrics?.safety_score || 100}</p>
              </div>
              <div className={`p-3 rounded-lg ${getScoreColor(metrics?.safety_score || 100)}`}>
                <Award className="w-6 h-6" />
              </div>
            </div>
            <Badge className={`mt-2 ${getGradeColor(benchmark?.grade)}`}>
              Grade: {benchmark?.grade || 'A'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">TCIR</p>
                <p className="text-3xl font-bold">{metrics?.tcir?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Industry Avg</p>
                <p className="text-sm font-medium">{benchmark?.industry_benchmark?.tcir || 'N/A'}</p>
              </div>
            </div>
            {benchmark?.comparison?.tcir?.status === 'better' ? (
              <div className="flex items-center text-green-600 text-sm mt-2">
                <TrendingDown className="w-4 h-4 mr-1" />
                {Math.abs(benchmark?.comparison?.tcir?.difference_pct || 0)}% below industry
              </div>
            ) : (
              <div className="flex items-center text-red-600 text-sm mt-2">
                <TrendingUp className="w-4 h-4 mr-1" />
                {Math.abs(benchmark?.comparison?.tcir?.difference_pct || 0)}% above industry
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">DART Rate</p>
                <p className="text-3xl font-bold">{metrics?.dart_rate?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Industry Avg</p>
                <p className="text-sm font-medium">{benchmark?.industry_benchmark?.dart_rate || 'N/A'}</p>
              </div>
            </div>
            {benchmark?.comparison?.dart_rate?.status === 'better' ? (
              <div className="flex items-center text-green-600 text-sm mt-2">
                <TrendingDown className="w-4 h-4 mr-1" />
                {Math.abs(benchmark?.comparison?.dart_rate?.difference_pct || 0)}% below industry
              </div>
            ) : (
              <div className="flex items-center text-red-600 text-sm mt-2">
                <TrendingUp className="w-4 h-4 mr-1" />
                {Math.abs(benchmark?.comparison?.dart_rate?.difference_pct || 0)}% above industry
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">EMR</p>
                <p className="text-3xl font-bold">{metrics?.emr?.toFixed(2) || '1.00'}</p>
              </div>
              <div className={`p-3 rounded-lg ${metrics?.emr < 1.0 ? 'bg-green-100 text-green-600' : metrics?.emr > 1.0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                <BarChart3 className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Experience Modification Rate</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="metrics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Safety Metrics {new Date().getFullYear()}</CardTitle>
                  <CardDescription>Update your company&apos;s safety metrics</CardDescription>
                </div>
                <Button onClick={() => setEditMode(!editMode)} variant="outline">
                  {editMode ? 'Cancel' : 'Edit Metrics'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Total Hours Worked</Label>
                  <Input
                    type="number"
                    value={metricsForm.total_hours_worked}
                    onChange={e => setMetricsForm({...metricsForm, total_hours_worked: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Employees</Label>
                  <Input
                    type="number"
                    value={metricsForm.total_employees}
                    onChange={e => setMetricsForm({...metricsForm, total_employees: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Recordable Incidents</Label>
                  <Input
                    type="number"
                    value={metricsForm.recordable_incidents}
                    onChange={e => setMetricsForm({...metricsForm, recordable_incidents: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>DART Cases</Label>
                  <Input
                    type="number"
                    value={metricsForm.dart_cases}
                    onChange={e => setMetricsForm({...metricsForm, dart_cases: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Days Away from Work</Label>
                  <Input
                    type="number"
                    value={metricsForm.days_away}
                    onChange={e => setMetricsForm({...metricsForm, days_away: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Restricted Work Days</Label>
                  <Input
                    type="number"
                    value={metricsForm.restricted_days}
                    onChange={e => setMetricsForm({...metricsForm, restricted_days: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Near Misses Reported</Label>
                  <Input
                    type="number"
                    value={metricsForm.near_misses}
                    onChange={e => setMetricsForm({...metricsForm, near_misses: parseInt(e.target.value) || 0})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>EMR</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={metricsForm.emr}
                    onChange={e => setMetricsForm({...metricsForm, emr: parseFloat(e.target.value) || 1.0})}
                    disabled={!editMode}
                  />
                </div>
              </div>
              {editMode && (
                <Button onClick={handleUpdateMetrics} className="mt-6">
                  Save Metrics
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>5-Year Safety Trends</CardTitle>
              <CardDescription>Historical safety performance</CardDescription>
            </CardHeader>
            <CardContent>
              {trends.length > 0 ? (
                <div className="space-y-4">
                  {trends.map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{t.year}</p>
                        <p className="text-sm text-gray-500">Hours: {t.total_hours_worked?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">TCIR</p>
                        <p className="font-bold">{t.tcir?.toFixed(2) || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">DART</p>
                        <p className="font-bold">{t.dart_rate?.toFixed(2) || 0}</p>
                      </div>
                      <div>
                        <Badge className={getScoreColor(t.safety_score)}>
                          Score: {t.safety_score || 100}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No historical data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <CardTitle>Safety Leaderboard</CardTitle>
              <CardDescription>Top companies by safety score</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {leaderboard.map((c, idx) => (
                  <div key={c.company_id} className={`flex items-center justify-between p-4 rounded-lg ${idx === 0 ? 'bg-yellow-50 border-yellow-200 border' : 'border'}`}>
                    <div className="flex items-center gap-4">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${idx === 0 ? 'bg-yellow-400 text-white' : idx === 1 ? 'bg-gray-300 text-white' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-gray-100'}`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{c.company_name}</p>
                        <p className="text-sm text-gray-500">TCIR: {c.tcir?.toFixed(2)} | DART: {c.dart_rate?.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getGradeColor(c.grade)}>{c.grade}</Badge>
                      <span className="text-2xl font-bold">{c.safety_score}</span>
                    </div>
                  </div>
                ))}
                {leaderboard.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No leaderboard data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SafetyScorecardPage;
